import React from 'react';
import { Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { useAtomValue } from 'jotai';
import { EditOutlined } from '@ant-design/icons';
import { Retry } from '@components/common';
import { tid } from '@utils/test';
import { preserveOrder } from '@components/common/DnD/utils';
import kpiStore from '@globalStore/kpiStore';
import { selectedMetricChart, setMetrics } from '@store/metricsChartContainer/actions';
import { selectedMetricSelector, metricSelector } from '@store/metricsChartContainer/selectors';
import { METRIC_SWITCH_MAP, getMetricDisplayName } from '@utils/metrics';
import track, { SELECTED_SUMMARY_PANEL_METRIC } from '@src/utils/sensor/track';
import { summaryWidgetsSelector } from '@store/summaryWidgets/selector';
import ToggleIcon from '@icons/common/toggle.svg';
import { dodAtom } from '@components/metrics-panel-hz/atoms';
import { IMPROVEMENT_OPPORTUNITY_METRICS } from '@src/utils/kpi';
import { filterNAMetricsAtom } from '@src/atoms/page';
import useWidgetsToRender from './hooks/useWidgetsToRender';
import { getWidgetOrder } from './utils';
import useFetchWidgetPanelData from './hooks/useFetchSummaryPanelData';
import { areAllWidgetsNotApplicable } from './widgets/edit-metrics/utils';
import { SPI_EXPORT_METRICS, WIDGET_TYPES, SPI_METRIC } from './widgets/constants';
import useSetSummaryExport from './hooks/useSetSummaryExport';
import { useFetchDod } from './hooks/useFetchDod';
import EditMetrics from './widgets/edit-metrics';
import SessionsWidget from './widgets/sessions-widget';
import ImprovementOpportunitiesWidget from './widgets/improvement-opportunities-widget';
import MetricsWidget from './widgets/metrics-widget';
import SPIWidget from './widgets/spi-widget';
import { WidgetsPanelCardsWrapper, WidgetsPanelContainerWrapper } from './styled';
import { doesFilterContainsInvalidField } from '@src/hooks/useSetError';
import { getFirstAppliedFilter } from '@src/store/common/selectors';

const widgetComponentMap = {
  [WIDGET_TYPES.spi.name]: SPIWidget,
  [WIDGET_TYPES.streams.name]: SessionsWidget,
  [WIDGET_TYPES.improvementOpportunities.name]: ImprovementOpportunitiesWidget,
  [WIDGET_TYPES.audienceMetrics.name]: MetricsWidget,
};

const WidgetPanel = () => {
  const currentKpi = kpiStore((state) => state.currentKpi);
  const dispatch = useDispatch();
  const summaryWidgets = useSelector(summaryWidgetsSelector);
  const dodEnabled = useAtomValue(dodAtom);
  const selectedMetric = useSelector(selectedMetricSelector);
  const metricsList = useSelector(metricSelector);
  const metricsToNA: string[] = useAtomValue(filterNAMetricsAtom);
  const { widgetMap = {} } = useWidgetsToRender(summaryWidgets.widgets, metricsToNA);
  const widgetMetricList: string[] = [];
  summaryWidgets.widgets.forEach(({ enabled, config }) =>
    enabled ? widgetMetricList.push(...config.metrics) : widgetMetricList,
  );
  const { isFetching, isError, data, refetch } = useFetchWidgetPanelData(
    widgetMetricList,
    currentKpi,
  );

  const {
    isFetching: isFetchingDod,
    isError: isDodError,
    data: dodData,
  } = useFetchDod({
    enabled: true,
    metricsList: widgetMetricList.filter((m) => !IMPROVEMENT_OPPORTUNITY_METRICS.includes(m)),
  });

  const totalsData = data?.totals;
  const dodTotals = dodData?.totals;

  const spiDisplayName = getMetricDisplayName({ metricKey: SPI_METRIC });

  // maybe put spi and streams widget in one parent and call useSetExport in it
  useSetSummaryExport({
    totals: totalsData,
    dodTotals,
    currentKpi,
    category: {
      name: spiDisplayName,
      metrics: SPI_EXPORT_METRICS.filter((metric) => widgetMetricList.includes(metric)),
    },
  });

  const onWidgetCardClick = (metricKey, value, metricTrackKey = null) => {
    if (selectedMetric !== metricKey && selectedMetric !== METRIC_SWITCH_MAP[metricKey]) {
      if (!metricsList.includes(metricKey) && !metricsList.includes(METRIC_SWITCH_MAP[metricKey])) {
        dispatch(setMetrics(preserveOrder(metricsList, [...metricsList, metricKey])));
      }

      track({
        event: SELECTED_SUMMARY_PANEL_METRIC,
        data: { metric: metricTrackKey ?? metricKey, value },
      });

      dispatch(selectedMetricChart(metricKey, undefined));
    }
  };
  const appliedFilter = useSelector(getFirstAppliedFilter);
  const filterError = doesFilterContainsInvalidField(appliedFilter);

  return (
    <WidgetsPanelContainerWrapper {...tid('widgets-panel')}>
      <Retry
        isLoading={isFetching || isFetchingDod}
        showError={isError || isDodError}
        fetchData={refetch}
        progress={true}
        queryError={filterError}
      >
        <WidgetsPanelCardsWrapper>
          {getWidgetOrder(summaryWidgets.widgets).map(({ widgetKey }) => {
            const WidgetComponent = widgetComponentMap?.[widgetKey];
            const metricList = widgetMap[widgetKey] || [];

            return metricList.length ? (
              <WidgetComponent
                key={widgetKey}
                currentKpi={currentKpi}
                data={totalsData}
                dodData={dodTotals}
                metricList={metricList}
                selectedChartMetric={selectedMetric}
                showTrend={dodEnabled}
                onWidgetCardClick={onWidgetCardClick}
              />
            ) : null;
          })}
        </WidgetsPanelCardsWrapper>
      </Retry>

      {/* empty summary widgets logic */}
      {!widgetMetricList.length ||
      areAllWidgetsNotApplicable(summaryWidgets.widgets, metricsToNA) ? (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ alignSelf: 'center', margin: '10px 0 0' }}>
            {!widgetMetricList.length
              ? 'No summary metrics selected!'
              : 'Selected summary metrics are not applicable'}
          </h3>
          <p style={{ alignSelf: 'center' }}>
            {'Please add a metric from the edit option '}
            {<EditOutlined />}
            {' or hide the summary panel using page sections from top right toggle '}
            {<Button disabled={true} style={{ cursor: 'unset' }} icon={<ToggleIcon />} />}
          </p>
        </div>
      ) : null}

      <EditMetrics />
    </WidgetsPanelContainerWrapper>
  );
};

export default WidgetPanel;
